import React, { useEffect, useState } from 'react';
import { Form, Input, Checkbox } from 'antd';
import AuthButton, { AuthSpinner } from '../../components/AuthButton';
import LogoUpload from './LogoUpload';
import OtpInput from './OtpInput';
import type { UploadFile } from 'antd';
import {
  BankOutlined,
  NumberOutlined,
  UserOutlined,
  MailOutlined,
  LockOutlined,
  SafetyOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { type AppDispatch } from '../../redux/store';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../components/ToastComponent/store/toastMiddleware';
import BrandLogo from '../../assets/images/goldpulse_logo.png';
import { getStrength } from './authUtils';
import {
  checkOrganizationCodeAvailable,
  resendSignUpOtp,
  signUp,
  verifySignUpOtp,
  type OtpSentResponse,
  type SignUpPayload,
} from './signupApi';
import AuthHero from '../../components/AuthHero';
import JewelBackdrop from '../../components/AuthHero/JewelBackdrop';
import '../SignIn/index.scss';
import './index.scss';

interface SignUpValues {
  organizationLogo?: UploadFile[];
  organizationName: string;
  organizationCode: string;
  firstName: string;
  lastName: string;
  userName: string;
  email: string;
  contactNumber: string;
  password: string;
  confirmPassword: string;
  agree: boolean;
}

const iconStyle = { color: '#64748B', marginRight: 8 };

const OTP_LENGTH = 6;

// API field name -> form field name (only where they differ)
const FIELD_MAP: Record<string, keyof SignUpValues> = {
  phoneNumber: 'contactNumber',
  termsAccepted: 'agree',
};

const FORM_FIELDS = [
  'userName',
  'email',
  'phoneNumber',
  'termsAccepted',
  'organizationCode',
];

const DUPLICATE_MESSAGE =
  'Username, email or phone number is already registered';
const GENERIC_MESSAGE = 'Something went wrong. Please try again.';
const TERMS_MESSAGE = 'You must accept the terms and conditions';

const readAsDataUrl = async (file: Blob): Promise<string> =>
  await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(String(reader.result));
    };
    reader.onerror = () => {
      reject(reader.error);
    };
    reader.readAsDataURL(file);
  });

const formatTime = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const SignUpPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [form] = Form.useForm<SignUpValues>();

  const password = Form.useWatch('password', form);
  const strength = getStrength(password);

  // The form stays mounted (hidden) on the OTP step, so entered data is kept in memory only
  const [step, setStep] = useState<'form' | 'otp'>('form');
  const [email, setEmail] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [expiresLeft, setExpiresLeft] = useState(0);
  const [resendLeft, setResendLeft] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // Expiry + resend countdowns
  useEffect(() => {
    if (expiresLeft <= 0 && resendLeft <= 0) return;
    const timer = window.setTimeout(() => {
      setExpiresLeft((s) => Math.max(s - 1, 0));
      setResendLeft((s) => Math.max(s - 1, 0));
    }, 1000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [expiresLeft, resendLeft]);

  const isExpired = step === 'otp' && expiresLeft <= 0;
  const canResend = resendLeft <= 0 || isExpired;

  const goToSignIn = () => {
    navigate('/sign-in');
  };

  const startOtpTimers = (res: OtpSentResponse) => {
    setMaskedEmail(res.email);
    setExpiresLeft(res.expiresInSeconds);
    setResendLeft(res.resendAfterSeconds);
    setOtp('');
    setOtpError('');
  };

  const backToForm = () => {
    setStep('form');
    setOtp('');
    setOtpError('');
  };

  // Shows API field errors under the matching form input. Returns true if one was shown.
  const showFieldErrors = (
    entries: Array<{ field: string; message: string }>
  ): boolean => {
    if (entries.length === 0) return false;
    form.setFields(
      entries.map(({ field, message }) => ({
        name: FIELD_MAP[field] ?? (field as keyof SignUpValues),
        errors: [message],
      }))
    );
    return true;
  };

  // Joi/celebrate validation error, or { status, message, field } for a known form field
  const extractFormErrors = (data: any) => {
    const keys: string[] | undefined = data?.validation?.body?.keys;
    if (Array.isArray(keys) && keys.length > 0) {
      return keys.map((key) => ({
        field: key,
        message:
          key === 'termsAccepted'
            ? TERMS_MESSAGE
            : (data?.validation?.body?.message ?? 'Invalid value'),
      }));
    }
    if (
      typeof data?.field === 'string' &&
      FORM_FIELDS.includes(String(data.field))
    ) {
      return [{ field: data.field as string, message: String(data.message) }];
    }
    return [];
  };

  const onFinish = async (values: SignUpValues) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const logoFile = values.organizationLogo?.[0]?.originFileObj;
      const payload: SignUpPayload = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        userName: values.userName.trim(),
        email: values.email.trim(),
        phoneNumber: values.contactNumber.trim(),
        password: values.password,
        termsAccepted: values.agree,
        organizationName: values.organizationName.trim(),
        organizationCode: values.organizationCode.trim(),
      };
      if (logoFile !== undefined) {
        payload.organizationLogo = await readAsDataUrl(logoFile);
      }

      const res = await signUp(payload);
      setEmail(payload.email);
      startOtpTimers(res);
      setStep('otp');
    } catch (error: any) {
      const data = error?.response?.data;
      const status = error?.response?.status;
      if (status === 400 && showFieldErrors(extractFormErrors(data))) return;
      if (data?.message === 'Duplicate key error') {
        dispatch(showErrorMessage(DUPLICATE_MESSAGE));
      } else if (typeof data?.message === 'string') {
        dispatch(showErrorMessage(data.message));
      } else {
        dispatch(showErrorMessage(GENERIC_MESSAGE));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onVerify = async () => {
    if (isVerifying) return;
    if (otp.length < OTP_LENGTH) {
      setOtpError(`Enter the ${OTP_LENGTH}-digit code`);
      return;
    }
    setIsVerifying(true);
    try {
      await verifySignUpOtp({ email, otp });
      form.resetFields();
      dispatch(showSuccessMessage('Account created. Please sign in.'));
      goToSignIn();
    } catch (error: any) {
      const data = error?.response?.data;
      const status = error?.response?.status;
      const message: string =
        typeof data?.message === 'string' ? data.message : GENERIC_MESSAGE;

      if (status === 429) {
        // Pending signup is deleted after too many wrong attempts
        dispatch(showErrorMessage(message));
        backToForm();
      } else if (
        status === 400 &&
        typeof data?.field === 'string' &&
        FORM_FIELDS.includes(String(data.field))
      ) {
        // Username / email / phone / branch code taken while the OTP was pending
        backToForm();
        showFieldErrors([{ field: data.field, message }]);
      } else if (status === 400 && /already used/i.test(message)) {
        setExpiresLeft(0);
        setOtp('');
        setOtpError('OTP expired. Please request a new one');
      } else if (status === 400 && /expired/i.test(message)) {
        setExpiresLeft(0);
        setOtp('');
        setOtpError(message);
      } else if (status === 400) {
        setOtp('');
        setOtpError(message);
      } else {
        dispatch(showErrorMessage(message));
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const onResend = async () => {
    if (!canResend || isResending) return;
    setIsResending(true);
    try {
      const res = await resendSignUpOtp({ email });
      startOtpTimers(res);
    } catch (error: any) {
      const data = error?.response?.data;
      const status = error?.response?.status;
      const message: string =
        typeof data?.message === 'string' ? data.message : GENERIC_MESSAGE;
      dispatch(showErrorMessage(message));
      if (status === 404 || status === 500) {
        // Pending signup is gone: start again from the form
        backToForm();
      }
    } finally {
      setIsResending(false);
    }
  };

  const signInLink = (
    <span
      className="signup-link"
      role="link"
      tabIndex={0}
      onClick={goToSignIn}
      onKeyDown={(e) => {
        if (e.key === 'Enter') goToSignIn();
      }}
    >
      Sign in
    </span>
  );

  return (
    <div className="sign-in-container sign-up-page">
      <div className="bg-stripe" />
      <div className="bg-red">
        <JewelBackdrop />
        <span className="orb orb-1" />
        <span className="orb orb-2" />
      </div>

      <header className="top-bar">
        <div className="brand">
          <img src={BrandLogo} alt="Gold Pulse" />
        </div>
        <div className="request-pill">
          Already have an account? {signInLink}
        </div>
      </header>

      <AuthHero />

      <div className="form-container">
        <div className={`form-card${step === 'otp' ? ' form-card--otp' : ''}`}>
          <div className="signup-step" hidden={step !== 'form'}>
            <p className="eyebrow">
              <SafetyOutlined /> SECURE SIGN-UP
            </p>
            <p className="title">Create your account</p>
            <p className="subtitle">
              Register your organization to get started.
            </p>

            <Form
              form={form}
              name="signup_form"
              layout="vertical"
              requiredMark={(label, { required }) => (
                <>
                  {label}
                  {required && <span className="signup-req">*</span>}
                </>
              )}
              onFinish={onFinish}
            >
              <div className="signup-grid">
                <Form.Item
                  className="full"
                  label="Organization logo"
                  name="organizationLogo"
                  rules={[
                    {
                      required: true,
                      message: 'Please upload organization logo',
                    },
                  ]}
                >
                  <LogoUpload />
                </Form.Item>

                <div className="full org-row">
                  <Form.Item
                    className="org-name"
                    label="Organization name"
                    name="organizationName"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter organization name',
                      },
                    ]}
                  >
                    <Input
                      className="signin__input"
                      placeholder="Your company name"
                      prefix={<BankOutlined style={iconStyle} />}
                    />
                  </Form.Item>

                  <Form.Item
                    className="org-code"
                    label="Code"
                    name="organizationCode"
                    validateDebounce={500}
                    normalize={(value: string) =>
                      String(value ?? '')
                        .replace(/[^a-zA-Z]/g, '')
                        .toUpperCase()
                    }
                    rules={[
                      {
                        required: true,
                        message: 'Please enter organization code',
                      },
                      {
                        pattern: /^[A-Z]{3}$/,
                        message: 'Organization code must be 3 letters (e.g. KRR)',
                      },
                      {
                        validator: async (_: unknown, value: string) => {
                          if (!/^[A-Z]{3}$/.test(value ?? '')) return;
                          let available = true;
                          try {
                            ({ available } =
                              await checkOrganizationCodeAvailable(value));
                          } catch {
                            // don't block: the server checks again on submit
                            return;
                          }
                          if (!available) {
                            throw new Error('Please choose a different code');
                          }
                        },
                      },
                    ]}
                  >
                    <Input
                      className="signin__input"
                      placeholder="e.g. KRR"
                      maxLength={3}
                      prefix={<NumberOutlined style={iconStyle} />}
                    />
                  </Form.Item>
                </div>

                <Form.Item
                  label="First name"
                  name="firstName"
                  rules={[
                    { required: true, message: 'Please enter first name' },
                  ]}
                >
                  <Input
                    className="signin__input"
                    placeholder="First name"
                    prefix={<UserOutlined style={iconStyle} />}
                  />
                </Form.Item>

                <Form.Item
                  label="Last name"
                  name="lastName"
                  rules={[
                    { required: true, message: 'Please enter last name' },
                  ]}
                >
                  <Input
                    className="signin__input"
                    placeholder="Last name"
                    prefix={<UserOutlined style={iconStyle} />}
                  />
                </Form.Item>

                <Form.Item
                  className="full"
                  label="Username"
                  name="userName"
                  rules={[
                    { required: true, message: 'Please choose a username' },
                    {
                      pattern: /^[^\sA-Z]+$/,
                      message: 'Username must be lowercase with no spaces',
                    },
                  ]}
                >
                  <Input
                    className="signin__input"
                    placeholder="Choose a username"
                    prefix={<UserOutlined style={iconStyle} />}
                  />
                </Form.Item>

                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Enter a valid email' },
                  ]}
                >
                  <Input
                    className="signin__input"
                    placeholder="name@company.com"
                    prefix={<MailOutlined style={iconStyle} />}
                  />
                </Form.Item>

                <Form.Item
                  label="Contact number"
                  name="contactNumber"
                  rules={[
                    { required: true, message: 'Please enter contact number' },
                    {
                      pattern: /^[6-9]\d{9}$/,
                      message: 'Enter a valid 10-digit mobile number',
                    },
                  ]}
                >
                  <Input
                    className="signin__input"
                    placeholder="98765 43210"
                    maxLength={10}
                    prefix={<span className="phone-code">+91</span>}
                  />
                </Form.Item>

                <Form.Item
                  label="Password"
                  name="password"
                  rules={[
                    { required: true, message: 'Please create a password' },
                    {
                      pattern: /^(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/,
                      message: 'Password does not meet the requirements',
                    },
                  ]}
                >
                  <Input.Password
                    className="signin__input"
                    placeholder="Create a password"
                    prefix={<LockOutlined style={iconStyle} />}
                  />
                </Form.Item>

                <Form.Item
                  label="Confirm password"
                  name="confirmPassword"
                  dependencies={['password']}
                  rules={[
                    { required: true, message: 'Please re-enter the password' },
                    ({ getFieldValue }) => ({
                      validator: async (_, value: string | undefined) => {
                        if (
                          value === undefined ||
                          value === '' ||
                          getFieldValue('password') === value
                        ) {
                          return;
                        }
                        throw new Error('Passwords do not match');
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    className="signin__input"
                    placeholder="Re-enter password"
                    prefix={<LockOutlined style={iconStyle} />}
                  />
                </Form.Item>
              </div>

              <div className="strength-meter">
                {[1, 2, 3, 4].map((level) => (
                  <span
                    key={level}
                    className={`bar${strength >= level ? ` on level-${strength}` : ''}`}
                  />
                ))}
              </div>
              <p className="strength-hint">
                Use 8+ characters with a number and a symbol.
              </p>

              <Form.Item
                name="agree"
                valuePropName="checked"
                className="terms-item"
                rules={[
                  {
                    validator: async (
                      _: unknown,
                      value: boolean | undefined
                    ) => {
                      if (value === true) {
                        return;
                      }
                      throw new Error('Please accept the Terms & Conditions');
                    },
                  },
                ]}
              >
                <Checkbox>
                  I agree to the{' '}
                  <a href="/terms-of-service" target="_blank" rel="noreferrer">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="/privacy-policy" target="_blank" rel="noreferrer">
                    Privacy Policy
                  </a>
                </Checkbox>
              </Form.Item>

              <Form.Item>
                <AuthButton
                  htmlType="submit"
                  loading={isSubmitting}
                  loadingText="Creating account..."
                >
                  Create account <ArrowRightOutlined />
                </AuthButton>
              </Form.Item>
            </Form>
          </div>

          {step === 'otp' && (
            <div className="signup-step otp-step">
              <p className="eyebrow">
                <MailOutlined /> VERIFY EMAIL
              </p>
              <p className="title">Check your email</p>
              <p className="subtitle">
                We sent a {OTP_LENGTH}-digit code to{' '}
                <strong>{maskedEmail}</strong>. Enter it below to verify your
                account.
              </p>

              <OtpInput
                value={otp}
                onChange={(value) => {
                  setOtp(value);
                  setOtpError('');
                }}
                length={OTP_LENGTH}
                hasError={otpError !== ''}
              />
              <p className="otp-error">{otpError}</p>

              <p className="otp-resend">
                {isExpired ? (
                  <span className="otp-error-text">OTP expired</span>
                ) : (
                  <>
                    Code expires in{' '}
                    <span className="otp-timer">{formatTime(expiresLeft)}</span>
                  </>
                )}
              </p>

              <AuthButton
                onClick={() => {
                  void onVerify();
                }}

                disabled={otp.length < OTP_LENGTH || isExpired}
                loading={isVerifying}
                loadingText="Verifying..."
              >
                Verify &amp; continue <ArrowRightOutlined />
              </AuthButton>

              <p className="otp-resend">
                Didn&apos;t receive the code?{' '}
                {canResend ? (
                  <span
                    className="signup-link"
                    role="button"
                    tabIndex={0}
                    aria-disabled={isResending}
                    onClick={() => {
                      void onResend();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void onResend();
                    }}
                  >
                    {isResending ? (
                      <>
                        <AuthSpinner /> Sending...
                      </>
                    ) : (
                      'Resend code'
                    )}
                  </span>
                ) : (
                  <span className="otp-timer">
                    Resend in {formatTime(resendLeft)}
                  </span>
                )}
              </p>
              <p className="otp-back">
                <span
                  className="signup-link"
                  role="button"
                  tabIndex={0}
                  onClick={backToForm}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') backToForm();
                  }}
                >
                  ← Change email / Back
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
